import { useEffect, useMemo } from "react";
import {
  Button,
  Tag,
  cn,
} from "@jem-hr/jem-ui";
import {
  Download,
  Printer,
  X,
} from "lucide-react";

/* ================================================================== */
/*  URL param parsing                                                  */
/* ================================================================== */

type PreviewType = "planned-vs-actuals" | "timesheet" | "posting-sheet";

interface PreviewParams {
  type: PreviewType;
  scopeKind: "site" | "roster";
  scopeId: string;
  scopeLabel: string;
  scopeRegion?: string;
  scopeParent?: string;
  shift: string;
  dateFrom?: Date;
  dateTo?: Date;
  date?: Date;
}

function parseParams(search: string): PreviewParams | null {
  const p = new URLSearchParams(search);
  const type = p.get("type");
  if (type !== "planned-vs-actuals" && type !== "timesheet" && type !== "posting-sheet") {
    return null;
  }
  const scopeKind = p.get("scopeKind");
  if (scopeKind !== "site" && scopeKind !== "roster") return null;
  const scopeId = p.get("scopeId") ?? "";
  const scopeLabel = p.get("scopeLabel") ?? "";
  const shift = p.get("shift") ?? "all";
  const parse = (key: string) => {
    const v = p.get(key);
    return v ? new Date(v) : undefined;
  };
  return {
    type,
    scopeKind,
    scopeId,
    scopeLabel,
    scopeRegion: p.get("scopeRegion") ?? undefined,
    scopeParent: p.get("scopeParent") ?? undefined,
    shift,
    dateFrom: parse("dateFrom"),
    dateTo: parse("dateTo"),
    date: parse("date"),
  };
}

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function fmtDate(d: Date | undefined): string {
  if (!d) return "—";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtShortDate(d: Date): string {
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

function shiftLabel(shift: string): string {
  if (shift === "day") return "Day shift";
  if (shift === "night") return "Night shift";
  return "All shifts";
}

function daysBetween(from: Date, to: Date): Date[] {
  const days: Date[] = [];
  const cur = new Date(from);
  while (cur <= to) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

/* ================================================================== */
/*  Preview header (shared across report types)                        */
/* ================================================================== */

function PreviewHeader({
  title,
  subtitle,
  params,
  onDownload,
}: {
  title: string;
  subtitle: string;
  params: PreviewParams;
  onDownload: () => void;
}) {
  const dateRange = params.date
    ? fmtDate(params.date)
    : params.dateFrom && params.dateTo
    ? `${fmtDate(params.dateFrom)} – ${fmtDate(params.dateTo)}`
    : "—";

  return (
    <>
      {/* Toolbar — sticky, outside the "paper" */}
      <div className="sticky top-0 z-10 bg-white border-b border-greyscale-border">
        <div className="max-w-[960px] mx-auto px-md sm:px-lg py-sm flex flex-wrap items-center justify-between gap-sm">
          <div className="flex items-center gap-xs">
            <Tag variant="drafted" size="sm">Preview</Tag>
            <p className="text-xs text-greyscale-text-caption">
              This is a stubbed V1 preview — data shown is illustrative.
            </p>
          </div>
          <div className="flex items-center gap-xs w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => window.print()}
              leftIcon={<Printer size={16} />}
              className="flex-1 sm:flex-none"
            >
              Print
            </Button>
            <Button
              onClick={onDownload}
              leftIcon={<Download size={16} />}
              className="flex-1 sm:flex-none"
            >
              Download
            </Button>
            <button
              type="button"
              onClick={() => window.close()}
              aria-label="Close preview"
              className="rounded-md p-xs text-greyscale-text-caption hover:bg-neutral-50"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Document header — inside the "paper" */}
      <div className="mb-lg">
        <div className="flex flex-wrap items-start justify-between gap-sm">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption">
              {subtitle}
            </p>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-greyscale-text-title mt-xxxs break-words">
              {title}
            </h1>
          </div>
          <div className="text-sm text-greyscale-text-body text-right">
            <p className="font-semibold text-greyscale-text-title">{params.scopeLabel}</p>
            {params.scopeRegion && (
              <p className="text-xs text-greyscale-text-caption">{params.scopeRegion}</p>
            )}
            {params.scopeParent && (
              <p className="text-xs text-greyscale-text-caption">in {params.scopeParent}</p>
            )}
          </div>
        </div>

        <div className="mt-sm grid grid-cols-2 sm:grid-cols-4 gap-sm text-xs border-t border-greyscale-border pt-sm">
          <div>
            <p className="uppercase tracking-wide text-greyscale-text-caption">Scope</p>
            <p className="font-semibold text-greyscale-text-title capitalize">{params.scopeKind}</p>
          </div>
          <div>
            <p className="uppercase tracking-wide text-greyscale-text-caption">Period</p>
            <p className="font-semibold text-greyscale-text-title">{dateRange}</p>
          </div>
          <div>
            <p className="uppercase tracking-wide text-greyscale-text-caption">Shift</p>
            <p className="font-semibold text-greyscale-text-title">{shiftLabel(params.shift)}</p>
          </div>
          <div>
            <p className="uppercase tracking-wide text-greyscale-text-caption">Generated</p>
            <p className="font-semibold text-greyscale-text-title">{fmtDate(new Date())}</p>
          </div>
        </div>
      </div>
    </>
  );
}

/* ================================================================== */
/*  Planned vs Actuals preview                                         */
/* ================================================================== */

function PlannedVsActualsPreview({ params, onDownload }: { params: PreviewParams; onDownload: () => void }) {
  const days = useMemo(() => {
    if (!params.dateFrom || !params.dateTo) return [];
    return daysBetween(params.dateFrom, params.dateTo).slice(0, 14);
  }, [params.dateFrom, params.dateTo]);

  // Deterministic mock data keyed on day index
  const dailyRows = days.map((d, i) => {
    const rostered = 26 + ((i * 7) % 6);
    const present = Math.max(5, Math.round(rostered * (0.3 + ((i * 13) % 50) / 100)));
    const schedHrs = rostered * 12;
    const actualHrs = Math.round(present * (9 + ((i * 3) % 3)) * 10) / 10;
    const attPct = Math.round((present / rostered) * 100);
    return { date: d, rostered, present, schedHrs, actualHrs, attPct };
  });

  const totals = dailyRows.reduce(
    (acc, r) => ({
      rostered: acc.rostered + r.rostered,
      present: acc.present + r.present,
      schedHrs: acc.schedHrs + r.schedHrs,
      actualHrs: acc.actualHrs + r.actualHrs,
    }),
    { rostered: 0, present: 0, schedHrs: 0, actualHrs: 0 },
  );

  const avgAttendance = totals.rostered > 0 ? Math.round((totals.present / totals.rostered) * 100) : 0;

  return (
    <>
      <PreviewHeader
        title="Planned vs Actuals"
        subtitle="Operational attendance report"
        params={params}
        onDownload={onDownload}
      />

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-sm mb-lg">
        <SummaryTile label="Rostered" value={String(totals.rostered)} unit="shifts" />
        <SummaryTile label="Present" value={String(totals.present)} unit="clocked in" />
        <SummaryTile label="Attendance" value={`${avgAttendance}%`} unit="avg" flag={avgAttendance < 50} />
        <SummaryTile label="Hours" value={`${Math.round(totals.actualHrs)} / ${totals.schedHrs}`} unit="actual / sched" />
      </div>

      {/* Daily tiles */}
      <h2 className="text-sm font-semibold text-greyscale-text-title mb-xs">Daily breakdown</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-xs mb-lg">
        {dailyRows.map((r) => (
          <div
            key={r.date.toISOString()}
            className={cn(
              "rounded-lg border p-sm",
              r.attPct < 50
                ? "border-red-300 bg-red-50"
                : "border-greyscale-border bg-white",
            )}
          >
            <p className="text-xs uppercase tracking-wide text-greyscale-text-caption">{fmtShortDate(r.date)}</p>
            <p className="text-lg font-semibold text-greyscale-text-title mt-xxxs">{r.attPct}%</p>
            <p className="text-xs text-greyscale-text-body">{r.present} of {r.rostered}</p>
            <p className="text-xs text-greyscale-text-caption mt-xxxs">{Math.round(r.actualHrs)}h / {r.schedHrs}h</p>
          </div>
        ))}
      </div>

      {/* Detail table — scrollable on mobile */}
      <h2 className="text-sm font-semibold text-greyscale-text-title mb-xs">Daily detail</h2>
      <div className="overflow-x-auto rounded-lg border border-greyscale-border">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-greyscale-border">
            <tr>
              <th className="text-left px-sm py-xs font-semibold text-greyscale-text-caption uppercase text-xs">Date</th>
              <th className="text-right px-sm py-xs font-semibold text-greyscale-text-caption uppercase text-xs">Rostered</th>
              <th className="text-right px-sm py-xs font-semibold text-greyscale-text-caption uppercase text-xs">Present</th>
              <th className="text-right px-sm py-xs font-semibold text-greyscale-text-caption uppercase text-xs">Att %</th>
              <th className="text-right px-sm py-xs font-semibold text-greyscale-text-caption uppercase text-xs">Sched Hrs</th>
              <th className="text-right px-sm py-xs font-semibold text-greyscale-text-caption uppercase text-xs">Actual Hrs</th>
            </tr>
          </thead>
          <tbody>
            {dailyRows.map((r) => (
              <tr key={r.date.toISOString()} className="border-b border-greyscale-border last:border-0">
                <td className="px-sm py-xs text-greyscale-text-title">{fmtShortDate(r.date)}</td>
                <td className="px-sm py-xs text-right text-greyscale-text-body">{r.rostered}</td>
                <td className="px-sm py-xs text-right text-greyscale-text-body">{r.present}</td>
                <td className={cn("px-sm py-xs text-right font-semibold", r.attPct < 50 ? "text-red-700" : "text-greyscale-text-title")}>
                  {r.attPct}%
                </td>
                <td className="px-sm py-xs text-right text-greyscale-text-body">{r.schedHrs}h</td>
                <td className="px-sm py-xs text-right text-greyscale-text-body">{Math.round(r.actualHrs)}h</td>
              </tr>
            ))}
            <tr className="bg-neutral-50 font-semibold">
              <td className="px-sm py-xs text-greyscale-text-title">Total</td>
              <td className="px-sm py-xs text-right">{totals.rostered}</td>
              <td className="px-sm py-xs text-right">{totals.present}</td>
              <td className="px-sm py-xs text-right">{avgAttendance}%</td>
              <td className="px-sm py-xs text-right">{totals.schedHrs}h</td>
              <td className="px-sm py-xs text-right">{Math.round(totals.actualHrs)}h</td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

function SummaryTile({ label, value, unit, flag }: { label: string; value: string; unit?: string; flag?: boolean }) {
  return (
    <div
      className={cn(
        "rounded-lg border p-sm",
        flag ? "border-red-300 bg-red-50" : "border-greyscale-border bg-white",
      )}
    >
      <p className="text-xs uppercase tracking-wide text-greyscale-text-caption">{label}</p>
      <p className={cn("text-xl sm:text-2xl font-semibold mt-xxxs", flag ? "text-red-700" : "text-greyscale-text-title")}>
        {value}
      </p>
      {unit && <p className="text-xs text-greyscale-text-caption">{unit}</p>}
    </div>
  );
}

/* ================================================================== */
/*  Timesheet preview                                                  */
/* ================================================================== */

interface TimesheetRow {
  emp: string;
  empNo: string;
  date: string;
  shiftType: "Day" | "Night";
  schedStart: string;
  schedEnd: string;
  clockIn: string;
  clockOut: string;
  hours: string;
  source: "Employee" | "Supervisor" | "Admin";
  status: "Complete" | "Incomplete" | "No Show" | "Replacement";
}

const MOCK_TIMESHEET: TimesheetRow[] = [
  { emp: "Alexandra Bohmer", empNo: "EMP-4021", date: "16 Mar 2026", shiftType: "Day", schedStart: "08:00", schedEnd: "17:00", clockIn: "08:06", clockOut: "17:06", hours: "9.00", source: "Supervisor", status: "Complete" },
  { emp: "Amber Smith", empNo: "EMP-4022", date: "16 Mar 2026", shiftType: "Day", schedStart: "08:00", schedEnd: "17:00", clockIn: "—", clockOut: "—", hours: "Incomplete", source: "Employee", status: "Incomplete" },
  { emp: "Dawie Blue", empNo: "EMP-4023", date: "16 Mar 2026", shiftType: "Day", schedStart: "08:00", schedEnd: "17:00", clockIn: "07:58", clockOut: "17:02", hours: "9.00", source: "Employee", status: "Complete" },
  { emp: "Rodrigue S", empNo: "EMP-4024", date: "16 Mar 2026", shiftType: "Day", schedStart: "08:00", schedEnd: "17:00", clockIn: "—", clockOut: "—", hours: "0.00", source: "Admin", status: "No Show" },
  { emp: "Mike Kyazze", empNo: "EMP-4031", date: "16 Mar 2026", shiftType: "Day", schedStart: "08:00", schedEnd: "17:00", clockIn: "08:15", clockOut: "17:05", hours: "8.75", source: "Supervisor", status: "Replacement" },
  { emp: "Alexandra Bohmer", empNo: "EMP-4021", date: "17 Mar 2026", shiftType: "Day", schedStart: "08:00", schedEnd: "17:00", clockIn: "08:02", clockOut: "17:10", hours: "9.00", source: "Employee", status: "Complete" },
  { emp: "Dawie Blue", empNo: "EMP-4023", date: "17 Mar 2026", shiftType: "Day", schedStart: "08:00", schedEnd: "17:00", clockIn: "07:55", clockOut: "—", hours: "Incomplete", source: "Employee", status: "Incomplete" },
  { emp: "Mike Kyazze", empNo: "EMP-4031", date: "17 Mar 2026", shiftType: "Night", schedStart: "18:00", schedEnd: "06:00", clockIn: "18:04", clockOut: "06:12", hours: "12.00", source: "Employee", status: "Complete" },
];

function statusTagVariant(status: TimesheetRow["status"]): "success" | "error" | "drafted" | "pending" {
  if (status === "Complete") return "success";
  if (status === "No Show") return "error";
  if (status === "Replacement") return "pending";
  return "drafted";
}

function TimesheetPreview({ params, onDownload }: { params: PreviewParams; onDownload: () => void }) {
  const completeRows = MOCK_TIMESHEET.filter((r) => r.status === "Complete").length;
  const totalSched = MOCK_TIMESHEET.length * 9;
  const totalActual = MOCK_TIMESHEET.filter((r) => r.hours !== "Incomplete" && r.hours !== "0.00")
    .reduce((acc, r) => acc + Number(r.hours), 0);

  return (
    <>
      <PreviewHeader
        title="Timesheet"
        subtitle="Payroll export"
        params={params}
        onDownload={onDownload}
      />

      {/* Summary row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-sm mb-lg">
        <SummaryTile label="Shifts" value={String(MOCK_TIMESHEET.length)} unit="rostered" />
        <SummaryTile label="Completion" value={`${Math.round((completeRows / MOCK_TIMESHEET.length) * 100)}%`} unit="complete" />
        <SummaryTile label="Scheduled" value={`${totalSched}h`} unit="hours" />
        <SummaryTile label="Actual" value={`${totalActual.toFixed(1)}h`} unit="hours confirmed" />
      </div>

      {/* Table */}
      <h2 className="text-sm font-semibold text-greyscale-text-title mb-xs">
        Shift detail
      </h2>
      <p className="text-xs text-greyscale-text-caption mb-sm">
        In the exported file, each site appears on its own sheet plus a summary sheet. This preview shows a combined view.
      </p>
      <div className="overflow-x-auto rounded-lg border border-greyscale-border">
        <table className="w-full text-xs sm:text-sm">
          <thead className="bg-neutral-50 border-b border-greyscale-border">
            <tr>
              <Th>Employee</Th>
              <Th>Emp #</Th>
              <Th>Date</Th>
              <Th>Shift</Th>
              <Th>Scheduled</Th>
              <Th>Actual</Th>
              <Th align="right">Hours</Th>
              <Th>Source</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {MOCK_TIMESHEET.map((r, i) => (
              <tr key={i} className="border-b border-greyscale-border last:border-0">
                <Td className="font-semibold">{r.emp}</Td>
                <Td mono>{r.empNo}</Td>
                <Td>{r.date}</Td>
                <Td>{r.shiftType}</Td>
                <Td mono>{r.schedStart} – {r.schedEnd}</Td>
                <Td mono>{r.clockIn} – {r.clockOut}</Td>
                <Td align="right" mono>{r.hours}</Td>
                <Td>{r.source}</Td>
                <Td>
                  <Tag variant={statusTagVariant(r.status)} size="sm">{r.status}</Tag>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Th({ children, align = "left" }: { children: React.ReactNode; align?: "left" | "right" }) {
  return (
    <th className={cn(
      "px-sm py-xs font-semibold text-greyscale-text-caption uppercase text-[10px] sm:text-xs whitespace-nowrap",
      align === "right" ? "text-right" : "text-left",
    )}>
      {children}
    </th>
  );
}

function Td({ children, align = "left", mono, className }: { children: React.ReactNode; align?: "left" | "right"; mono?: boolean; className?: string }) {
  return (
    <td className={cn(
      "px-sm py-xs text-greyscale-text-body whitespace-nowrap",
      align === "right" ? "text-right" : "text-left",
      mono && "font-mono",
      className,
    )}>
      {children}
    </td>
  );
}

/* ================================================================== */
/*  Posting Sheet preview                                              */
/* ================================================================== */

interface PostingRow {
  persNo: string;
  name: string;
  jobTitle: "Guard" | "Site Senior" | "SUP";
  assocNo: string;
  grade: "A" | "B" | "C";
  shiftHrs: string;
  shiftTime: string;
  status: "P" | "A" | "N/A";
  replacedBy?: string;
  timeOn: string;
  siteObOn: string;
  controlRefOn: string;
  timeOff: string;
  siteObOff: string;
  controlRefOff: string;
  faPermit: string;
}

const MOCK_POSTING: PostingRow[] = [
  { persNo: "PG878", name: "ZUNGU VUYISILE", jobTitle: "Site Senior", assocNo: "2314675", grade: "B", shiftHrs: "12:00", shiftTime: "06:00-18:00", status: "P", timeOn: "05:50", siteObOn: "800", controlRefOn: "1060", timeOff: "18:05", siteObOff: "812", controlRefOff: "1082", faPermit: "—" },
  { persNo: "PG210", name: "BOTYA AKHANELE", jobTitle: "Guard", assocNo: "1253034", grade: "C", shiftHrs: "12:00", shiftTime: "06:00-18:00", status: "P", timeOn: "08:00", siteObOn: "801", controlRefOn: "1061", timeOff: "18:02", siteObOff: "813", controlRefOff: "1083", faPermit: "—" },
  { persNo: "PG210", name: "MASETI TEMBEKA", jobTitle: "Guard", assocNo: "1253034", grade: "C", shiftHrs: "12:00", shiftTime: "06:00-18:00", status: "A", replacedBy: "MENZIWAS S", timeOn: "05:50", siteObOn: "802", controlRefOn: "1062", timeOff: "18:00", siteObOff: "814", controlRefOff: "1084", faPermit: "—" },
  { persNo: "PG616", name: "MINDI MZIMASI", jobTitle: "Guard", assocNo: "1253034", grade: "C", shiftHrs: "12:00", shiftTime: "06:00-18:00", status: "P", timeOn: "05:50", siteObOn: "803", controlRefOn: "1063", timeOff: "17:58", siteObOff: "815", controlRefOff: "1085", faPermit: "FA-2021" },
  { persNo: "PG616", name: "MKHONTYIWE NOLUBABALO", jobTitle: "Guard", assocNo: "169855", grade: "C", shiftHrs: "—", shiftTime: "—", status: "N/A", timeOn: "—", siteObOn: "—", controlRefOn: "—", timeOff: "—", siteObOff: "—", controlRefOff: "—", faPermit: "—" },
  { persNo: "PG616", name: "NKUNZANA NOMVELISO", jobTitle: "Guard", assocNo: "169857", grade: "C", shiftHrs: "12:00", shiftTime: "06:00-18:00", status: "P", timeOn: "05:50", siteObOn: "804", controlRefOn: "1064", timeOff: "17:55", siteObOff: "816", controlRefOff: "1086", faPermit: "—" },
  { persNo: "PG619", name: "SIGA TRACY", jobTitle: "Guard", assocNo: "169838", grade: "C", shiftHrs: "12:00", shiftTime: "06:00-18:00", status: "P", timeOn: "05:50", siteObOn: "805", controlRefOn: "1065", timeOff: "18:10", siteObOff: "817", controlRefOff: "1087", faPermit: "FA-2044" },
  { persNo: "PG620", name: "YEKO ZINTLE", jobTitle: "Guard", assocNo: "169838", grade: "C", shiftHrs: "12:00", shiftTime: "06:00-18:00", status: "P", timeOn: "05:50", siteObOn: "806", controlRefOn: "1066", timeOff: "18:04", siteObOff: "818", controlRefOff: "1088", faPermit: "—" },
];

function PostingSheetPreview({ params, onDownload }: { params: PreviewParams; onDownload: () => void }) {
  const present = MOCK_POSTING.filter((r) => r.status === "P").length;
  const absent = MOCK_POSTING.filter((r) => r.status === "A").length;
  const na = MOCK_POSTING.filter((r) => r.status === "N/A").length;

  return (
    <>
      <PreviewHeader
        title="Posting Sheet"
        subtitle="Daily guard posting"
        params={params}
        onDownload={onDownload}
      />

      <div className="grid grid-cols-3 gap-sm mb-lg">
        <SummaryTile label="Present" value={String(present)} unit="P" />
        <SummaryTile label="Absent" value={String(absent)} unit="A" flag={absent > 0} />
        <SummaryTile label="N/A" value={String(na)} unit="—" />
      </div>

      <div className="overflow-x-auto rounded-lg border border-greyscale-border">
        <table className="w-full text-xs">
          <thead className="bg-neutral-50 border-b border-greyscale-border">
            <tr>
              <Th>Pers No</Th>
              <Th>Name</Th>
              <Th>Job</Th>
              <Th>Assoc</Th>
              <Th>Grade</Th>
              <Th>Shift Hrs</Th>
              <Th>Shift Time</Th>
              <Th>Status</Th>
              <Th>Replaced By</Th>
              <Th>Time On</Th>
              <Th>OB (On)</Th>
              <Th>Ctrl (On)</Th>
              <Th>Time Off</Th>
              <Th>OB (Off)</Th>
              <Th>Ctrl (Off)</Th>
              <Th>FA Permit</Th>
            </tr>
          </thead>
          <tbody>
            {MOCK_POSTING.map((r, i) => {
              const rowClass = r.jobTitle === "Site Senior" || r.jobTitle === "SUP"
                ? "bg-secondary-pink-50"
                : "";
              return (
                <tr key={i} className={cn("border-b border-greyscale-border last:border-0", rowClass)}>
                  <Td mono>{r.persNo}</Td>
                  <Td className="font-semibold">{r.name}</Td>
                  <Td>{r.jobTitle}</Td>
                  <Td mono>{r.assocNo}</Td>
                  <Td>{r.grade}</Td>
                  <Td mono>{r.shiftHrs}</Td>
                  <Td mono>{r.shiftTime}</Td>
                  <Td>
                    <Tag
                      variant={r.status === "P" ? "success" : r.status === "A" ? "error" : "drafted"}
                      size="sm"
                    >
                      {r.status}
                    </Tag>
                  </Td>
                  <Td>{r.replacedBy ?? "—"}</Td>
                  <Td mono>{r.timeOn}</Td>
                  <Td mono>{r.siteObOn}</Td>
                  <Td mono>{r.controlRefOn}</Td>
                  <Td mono>{r.timeOff}</Td>
                  <Td mono>{r.siteObOff}</Td>
                  <Td mono>{r.controlRefOff}</Td>
                  <Td mono>{r.faPermit}</Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-xl grid grid-cols-1 sm:grid-cols-2 gap-lg text-xs">
        <div>
          <p className="text-greyscale-text-caption uppercase tracking-wide">Supervisor signature</p>
          <div className="mt-sm border-b border-greyscale-text-caption h-12" />
        </div>
        <div>
          <p className="text-greyscale-text-caption uppercase tracking-wide">Control room sign-off</p>
          <div className="mt-sm border-b border-greyscale-text-caption h-12" />
        </div>
      </div>
    </>
  );
}

/* ================================================================== */
/*  Main entry                                                         */
/* ================================================================== */

export function ReportPreview() {
  const params = useMemo(() => parseParams(window.location.search), []);

  useEffect(() => {
    if (!params) {
      document.title = "Report preview";
      return;
    }
    const typeLabel =
      params.type === "planned-vs-actuals"
        ? "Planned vs Actuals"
        : params.type === "timesheet"
        ? "Timesheet"
        : "Posting Sheet";
    document.title = `${typeLabel} — ${params.scopeLabel}`;
  }, [params]);

  if (!params) {
    return (
      <div className="min-h-screen flex items-center justify-center p-lg">
        <div className="max-w-[420px] text-center">
          <h1 className="text-lg font-semibold text-greyscale-text-title">
            Missing or invalid preview parameters
          </h1>
          <p className="text-sm text-greyscale-text-body mt-xs">
            This tab was opened without the required query string. Close it and try again from the Reports wizard.
          </p>
        </div>
      </div>
    );
  }

  // Stubbed download — in production this would trigger the same download
  // pipeline as the wizard's Download button.
  const handleDownload = () => {
    window.alert(
      `Stubbed download — in production this would produce the ${params.type === "posting-sheet" ? "PDF" : "Excel"} file.`,
    );
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {params.type === "planned-vs-actuals" ? (
        <PreviewPaper>
          <PlannedVsActualsPreview params={params} onDownload={handleDownload} />
        </PreviewPaper>
      ) : params.type === "timesheet" ? (
        <PreviewPaper>
          <TimesheetPreview params={params} onDownload={handleDownload} />
        </PreviewPaper>
      ) : (
        <PreviewPaper>
          <PostingSheetPreview params={params} onDownload={handleDownload} />
        </PreviewPaper>
      )}
    </div>
  );
}

function PreviewPaper({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-[960px] mx-auto bg-white shadow-sm sm:my-lg sm:rounded-lg">
      <div className="px-md sm:px-lg py-lg">
        {children}
      </div>
    </div>
  );
}
